// src/hooks/useProductRecommendations.js
//
// Orchestrates the "Similar products" feed on the ProductDetails page:
//  1. Loads a bounded, cached candidate pool (no per-view catalogue reads).
//  2. Loads the viewer's wishlist ids (personalization by explicit saves only).
//  3. Ranks via the pure engine in utils/productRecommendations.js.
//
// Data fetching is keyed on the target product id so the result is refetched
// only when the user navigates to a different product. The candidate cache is
// shared across sessions by the repository, so back/forth navigation is cheap.

import { useEffect, useMemo, useRef, useState } from 'react';
import { recommendationsRepository } from '../repositories/recommendationsRepository';
import { rankProductRecommendations } from '../utils/productRecommendations';
import { errorDev } from '../utils/logger';

export const DEFAULT_LIMIT = 8;

/**
 * @param {object} product   the product currently being viewed
 * @param {object|null} currentUser  authenticated user (or null)
 * @param {{ limit?: number }} [opts]
 * @returns {{ recommendations: object[], loading: boolean, error: Error|null, retry: () => void }}
 */
export default function useProductRecommendations(product, currentUser = null, opts = {}) {
  const { limit = DEFAULT_LIMIT } = opts;
  const productId = product?.id ? String(product.id) : null;
  const buyerId = currentUser?.uid || null;
  const wishlistEnabled = Boolean(currentUser?.uid);

  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(Boolean(productId));
  const [error, setError] = useState(null);
  const loadedRef = useRef(new Set());

  useEffect(() => {
    if (!productId) {
      setRecommendations([]);
      setLoading(false);
      setError(null);
      return;
    }
    if (!product) {
      setRecommendations([]);
      setLoading(false);
      setError(null);
      return;
    }

    // Bail out when the product hasn't changed and we have results already;
    // avoids a flash + refetch on every re-render.
    const cacheKey = `${productId}:${buyerId || 'anon'}`;
    if (loadedRef.current.has(cacheKey) && recommendations.length > 0) {
      return;
    }
    loadedRef.current.add(cacheKey);

    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        const candidates = await recommendationsRepository.listCandidates();

        let wishlistIds = [];
        if (wishlistEnabled) {
          wishlistIds = await recommendationsRepository.listWishlistProductIds(buyerId);
        }

        if (!cancelled) {
          const { recommendations: ranked } = rankProductRecommendations({
            target: product,
            candidates,
            buyerId,
            wishlistProductIds: wishlistIds,
            limit,
          });
          setRecommendations(ranked);
        }
      } catch (err) {
        errorDev('useProductRecommendations: failed', err);
        if (!cancelled) setError(err instanceof Error ? err : new Error('Failed to load recommendations'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [productId, product, buyerId, wishlistEnabled, limit]);

  const retry = useMemo(
    () => () => {
      loadedRef.current.delete(`${productId}:${buyerId || 'anon'}`);
      // Force the candidate cache to refresh from Firestore.
      recommendationsRepository.clearCandidateCache();
    },
    [productId, buyerId]
  );

  return { recommendations, loading, error, retry };
}
